import React, { useEffect, useMemo, useState } from "react";
import { Languages, Rocket, Loader2, Check, X, Globe, Sparkles, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * TranslateBroadcastModal
 * ───────────────────────
 * One-tap multi-language broadcast. Clones a News Center story into
 * every selected language, regenerates the full multi-format package in
 * that language, then fires the standard 5-stage broadcast + optional
 * WordPress publish for every variant.
 *
 * Server contract: POST /api/news/stories/{story_id}/translate-broadcast
 *   { target_langs: string[], include_parent: bool, include_wp: bool,
 *     wp_status: "draft" | "publish", broadcast_options?: BroadcastIn }
 */
export default function TranslateBroadcastModal({
  open,
  onClose,
  story,
  parentLang = "en",
  broadcastOptions = null,
  onDone,
}) {
  const [catalog, setCatalog] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [includeParent, setIncludeParent] = useState(false);
  const [includeWp, setIncludeWp] = useState(true);
  const [wpPublishLive, setWpPublishLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/i18n/languages").then((r) => setCatalog(r.data)).catch(() => setCatalog({ launch: [], phase1: [], caribbean: [] }));
    // Reset state on open
    setPicked(new Set());
    setIncludeParent(false);
    setIncludeWp(true);
    setWpPublishLive(false);
    setResult(null);
  }, [open]);

  const toggle = (code) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  const groups = useMemo(() => ([
    { key: "caribbean", label: "// CARIBBEAN · USP",  rows: catalog?.caribbean || [], highlight: true },
    { key: "phase1",    label: "// PHASE 1",          rows: catalog?.phase1    || [] },
    { key: "launch",    label: "// LAUNCH",           rows: catalog?.launch    || [] },
  ]), [catalog]);

  const launch = async () => {
    const targets = [...picked].filter((c) => c !== parentLang);
    if (!targets.length && !includeParent) {
      toast.error("Pick at least one language (or enable Broadcast the original).");
      return;
    }
    setBusy(true);
    setResult(null);
    toast.message(`Cloning + broadcasting in ${targets.length} language${targets.length === 1 ? "" : "s"} — this runs in the background, ~${Math.max(15, targets.length * 25)}s per variant`);
    try {
      const { data: job } = await api.post(`/news/stories/${story.story_id}/translate-broadcast`, {
        target_langs: targets,
        include_parent: includeParent,
        include_wp: includeWp,
        wp_status: wpPublishLive ? "publish" : "draft",
        broadcast_options: broadcastOptions || undefined,
      });
      // Poll the job every 2s until done
      let last = null;
      while (true) {
        try {
          const { data: status } = await api.get(`/news/translate-broadcast-jobs/${job.job_id}`);
          last = status;
          setResult(status);   // live-update the UI as variants come in
          if (status.status === "done") break;
        } catch (pollErr) {
          console.error("poll error", pollErr);
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      const okCount = (last?.variants || []).filter((v) => !v.generate_error && !v.broadcast_error).length;
      toast.success(`Multi-language broadcast complete · ${okCount}/${last?.total || 0} variants shipped`);
      onDone?.(last);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error("AI budget exhausted — top up Universal Key.", { duration: 8000 });
      else toast.error(e?.response?.data?.detail || "Multi-language broadcast failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent
        className="max-w-2xl bg-bg-surface border-line text-ink-primary"
        data-testid="translate-broadcast-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display font-black tracking-tighter text-2xl">
            <Rocket className="h-6 w-6 text-brand-amber" />
            Translate + Broadcast
          </DialogTitle>
          <DialogDescription className="text-ink-muted">
            Clone this story into every language you pick, regenerate the multi-format
            package in each, then fire the same 5-stage broadcast cascade for all variants.
            Perfect for Caribbean multi-language reach.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                {result.status === "done"
                  ? <Check className="h-5 w-5 text-emerald-400" />
                  : <Loader2 className="h-5 w-5 animate-spin text-brand-amber" />}
                <span className="font-semibold">
                  {result.status === "done"
                    ? `${result.variants?.length || 0}/${result.total} variant${result.total === 1 ? "" : "s"} shipped`
                    : `Broadcasting… ${result.completed || 0}/${result.total} complete`}
                </span>
              </div>
              <span className="text-ink-muted text-xs">parent lang {result.parent_language}</span>
            </div>
            {result.status !== "done" && (
              <div className="w-full h-1 rounded bg-bg-elevated overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 via-purple-500 to-brand-amber transition-all"
                  style={{ width: `${((result.completed || 0) / Math.max(1, result.total)) * 100}%` }}
                />
              </div>
            )}
            <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
              {(result.variants || []).map((v) => (
                <VariantRow key={v.story_id} v={v} />
              ))}
              {result.status !== "done" && result.completed < result.total && (
                <div className="rounded-md border border-dashed border-line p-3 text-center text-[11px] text-ink-muted">
                  <Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1.5" />
                  Waiting on {result.total - (result.completed || 0)} more variant{result.total - result.completed === 1 ? "" : "s"}…
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-line">
              <button
                onClick={onClose}
                disabled={busy && result.status !== "done"}
                data-testid="translate-broadcast-close-result"
                className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm hover:text-brand-amber disabled:opacity-50"
              >{result.status === "done" ? "Done" : "Close (job keeps running)"}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Language checkboxes */}
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {!catalog && <div className="text-sm text-ink-muted flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading languages…</div>}
              {groups.map((g) => g.rows.length > 0 && (
                <div key={g.key}>
                  <div className={`text-[10px] font-mono uppercase tracking-[0.2em] mb-1.5 ${g.highlight ? "text-cyan-300" : "text-ink-muted"}`}>{g.label}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {g.rows.map((l) => {
                      const isPicked = picked.has(l.code);
                      const isParent = l.code === parentLang;
                      return (
                        <button
                          key={l.code}
                          onClick={() => !isParent && toggle(l.code)}
                          disabled={isParent || busy}
                          data-testid={`translate-lang-${l.code}`}
                          className={
                            "flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition disabled:opacity-40 " +
                            (isPicked
                              ? "border-brand-amber/60 bg-brand-amber/10 text-brand-amber"
                              : "border-line bg-bg-elevated hover:border-brand-amber/40 text-ink-primary")
                          }
                        >
                          <span className="text-lg leading-none">{l.flag}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-semibold truncate">{l.native}</span>
                            <span className="block text-[10px] text-ink-muted truncate">{l.name}{isParent ? " · original" : ""}</span>
                          </span>
                          {isPicked && <Check className="h-3.5 w-3.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Options */}
            <div className="space-y-2 rounded-md border border-line bg-bg-elevated p-3">
              <label className="flex items-center justify-between gap-3 text-xs cursor-pointer">
                <div>
                  <div className="font-semibold text-ink-primary">Also broadcast the original</div>
                  <div className="text-[11px] text-ink-muted">Fire the 5-stage cascade on the {parentLang.toUpperCase()} parent too</div>
                </div>
                <Switch
                  checked={includeParent}
                  onCheckedChange={setIncludeParent}
                  data-testid="translate-include-parent"
                  disabled={busy}
                />
              </label>
              <label className="flex items-center justify-between gap-3 text-xs cursor-pointer">
                <div>
                  <div className="font-semibold text-ink-primary flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-sky-300" /> Publish to WordPress</div>
                  <div className="text-[11px] text-ink-muted">Send each language variant to your connected WP site</div>
                </div>
                <Switch
                  checked={includeWp}
                  onCheckedChange={setIncludeWp}
                  data-testid="translate-include-wp"
                  disabled={busy}
                />
              </label>
              {includeWp && (
                <label className="flex items-center justify-between gap-3 text-xs cursor-pointer pl-4 border-l-2 border-sky-500/30">
                  <div>
                    <div className="font-semibold text-ink-primary">Publish immediately (skip draft)</div>
                    <div className="text-[11px] text-ink-muted">Off = save as draft in WP-Admin so you can review first</div>
                  </div>
                  <Switch
                    checked={wpPublishLive}
                    onCheckedChange={setWpPublishLive}
                    data-testid="translate-wp-live"
                    disabled={busy}
                  />
                </label>
              )}
            </div>

            {/* Launch */}
            <div className="flex items-center justify-between pt-2 border-t border-line">
              <div className="text-[11px] text-ink-muted flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-brand-amber" />
                {picked.size} language{picked.size === 1 ? "" : "s"} selected {includeParent && "· + original"}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  disabled={busy}
                  data-testid="translate-broadcast-cancel"
                  className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-ink-primary disabled:opacity-50"
                >Cancel</button>
                <button
                  onClick={launch}
                  disabled={busy || (!picked.size && !includeParent)}
                  data-testid="translate-broadcast-launch"
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-bold text-white disabled:opacity-50 transition hover:scale-[1.02]"
                  style={{
                    background: "linear-gradient(135deg, #8B5CF6 0%, #A855F7 50%, #FF8A1F 100%)",
                    boxShadow: "0 8px 24px -8px rgba(139,92,246,0.6)",
                  }}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                  {busy ? "Broadcasting…" : "Launch multi-lang broadcast"}
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function VariantRow({ v }) {
  const bc = v.broadcast || {};
  const stages = bc.stages || {};
  const wp = v.wp || {};
  const failed = !!(v.generate_error || v.broadcast_error);
  return (
    <div
      className={
        "rounded-md border p-3 " +
        (failed ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/30 bg-emerald-500/5")
      }
      data-testid={`variant-row-${v.language}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xl leading-none">{v.flag}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-ink-primary text-sm">{v.language_native || v.language_name}</div>
          <div className="text-[10px] font-mono uppercase text-ink-muted">{v.language} {v.is_parent && "· PARENT"}</div>
        </div>
        {failed
          ? <span className="rounded-md bg-rose-500/20 text-rose-300 px-2 py-0.5 text-[10px] font-mono uppercase">Failed</span>
          : <span className="rounded-md bg-emerald-500/20 text-emerald-300 px-2 py-0.5 text-[10px] font-mono uppercase">Shipped</span>}
      </div>
      {failed && (
        <div className="text-[11px] text-rose-300 flex items-center gap-1 mb-1">
          <AlertTriangle className="h-3 w-3" />
          {v.generate_error || v.broadcast_error}
        </div>
      )}
      {!failed && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[10px]">
          <StageChip label="Anchor" ok={stages.anchor_mix?.ok} skipped={stages.anchor_mix?.skipped} />
          <StageChip label="Video"  ok={stages.video?.ok}      skipped={stages.video?.skipped} />
          <StageChip label="Text"   ok={(stages.text_posts?.count || 0) > 0} extra={`${stages.text_posts?.count || 0}`} />
          <StageChip label="MP4"    ok={(stages.video_posts?.count || 0) > 0} extra={`${stages.video_posts?.count || 0}`} />
        </div>
      )}
      {v.wp !== undefined && (
        <div className="mt-1.5 flex items-center gap-1 text-[10px]">
          <Globe className="h-3 w-3 text-sky-300" />
          <span className="text-ink-muted uppercase font-mono">WP:</span>
          {wp.ok
            ? <a href={wp.url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">post #{wp.post_id} ↗</a>
            : <span className="text-rose-300">{wp.error || "not connected"}</span>}
        </div>
      )}
    </div>
  );
}

function StageChip({ label, ok, skipped, extra }) {
  const tone = ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
    : skipped ? "border-line text-ink-muted"
    : "border-rose-500/40 text-rose-300 bg-rose-500/5";
  return (
    <div className={`rounded border px-1.5 py-0.5 flex items-center justify-between font-mono uppercase ${tone}`}>
      <span>{label}</span>
      <span className="flex items-center gap-0.5">
        {ok && <Check className="h-2.5 w-2.5" />}
        {skipped && "—"}
        {!ok && !skipped && <X className="h-2.5 w-2.5" />}
        {extra && <span className="ml-0.5">{extra}</span>}
      </span>
    </div>
  );
}
